/**
 * Privacy Policy — public route at /legal/privacy
 * Used as the "Privacy Policy URL" in Google Play + App Store listings.
 * Intentionally plain HTML + Tailwind, no auth, no external dependencies —
 * so it renders even for logged-out users and is easily crawlable.
 */
import { Link } from "react-router-dom";

const LAST_UPDATED = "August 25, 2026";
const PUBLISHER = "Sashikunja Educational Services LLP";
const APP_NAME = "Kampus 365";
const SUPPORT_EMAIL = "klickapps2026@gmail.com";
const SCHOOL_EMAIL = "usacademy54648@gmail.com";
const SCHOOL_PHONE = "+91 99198 88882";
const SCHOOL_ADDRESS =
  "Rajpur, Campierganj, Gorakhpur, Uttar Pradesh 273158, India";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="privacy-policy-page">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <div className="mb-10">
          <Link
            to="/"
            className="text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900"
            data-testid="back-home-link"
          >
            ← Back to Kampus 365
          </Link>
          <h1 className="mt-4 text-4xl font-bold tracking-tight" data-testid="privacy-policy-heading">
            Privacy Policy
          </h1>
          <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>
        </div>

        <section className="max-w-none text-slate-700 leading-relaxed space-y-4 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-slate-900 [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-blue-600 [&_a]:underline hover:[&_a]:text-blue-800 [&_strong]:text-slate-900">
          <p>
            This Privacy Policy describes how <strong>{PUBLISHER}</strong> (“we,”
            “us,” or “our”), operator of the <strong>{APP_NAME}</strong> platform
            and mobile application, collects, uses, discloses, and protects
            information about you (“you” or “User”), whether you are a parent,
            student, teacher, or staff member of a school subscribed to our
            service.
          </p>

          <h2>1. Information We Collect</h2>

          <h3>1.1 Information you provide directly</h3>
          <ul>
            <li>
              <strong>Account information:</strong> name, email address, phone
              number, password (stored hashed), role (parent / student /
              teacher / admin), photo, date of birth.
            </li>
            <li>
              <strong>Student roster data:</strong> admission number, class,
              section, gender, blood group, address, KYC identifiers (Aadhaar,
              APAAR ID) where the school has chosen to store them.
            </li>
            <li>
              <strong>Family / contact data:</strong> parent/guardian names,
              contact numbers, emails, occupations, addresses.
            </li>
            <li>
              <strong>Content you post:</strong> homework text and attachments,
              notices, chat messages, attendance marks, exam marks.
            </li>
          </ul>

          <h3>1.2 Information collected automatically</h3>
          <ul>
            <li>
              <strong>Device & session data:</strong> device model, operating
              system, IP address, session tokens, timestamps of sign-in and
              activity.
            </li>
            <li>
              <strong>Location (teachers only, when enabled):</strong> GPS
              coordinates are captured <em>only</em> during staff check-in to
              enforce the school's 200-metre geofence. Location is not tracked
              continuously and is never shared with third parties.
            </li>
            <li>
              <strong>Camera / storage access (when you use it):</strong> photos
              and files you explicitly attach to homework, notices, or a
              profile photo. We do not scan or index your camera roll.
            </li>
          </ul>

          <h3>1.3 Information about children</h3>
          <p>
            {APP_NAME} is a school-management platform intended for use by
            schools with children under 18. All student data is collected and
            processed on the instruction of the school as the data controller.
            <strong> The school obtains parent consent as part of admission;
            we do not directly collect personal information from a child
            without that consent.</strong> Parents can request review, correction,
            or deletion of their child's data by contacting the school's
            institution administrator or by writing to {SUPPORT_EMAIL}.
          </p>

          <h2>2. How We Use Information</h2>
          <ul>
            <li>To provide the core school-management service (attendance, fees, homework, notices, timetable, report cards).</li>
            <li>To authenticate you and secure your account.</li>
            <li>To send transactional messages (e.g. fee receipts, notice alerts) via in-app inbox, email, SMS, or push notification.</li>
            <li>To enforce role-based access controls and campus-level data scoping.</li>
            <li>To generate aggregated, non-identifying analytics that help schools improve their operations.</li>
            <li>To comply with applicable laws and lawful requests from authorities.</li>
          </ul>
          <p>
            <strong>We do not sell your personal information, and we do not
            use children's data to serve targeted advertisements.</strong>
          </p>

          <h2>3. How We Share Information</h2>
          <p>Information is shared only in these limited situations:</p>
          <ul>
            <li>
              <strong>Within your school:</strong> Teachers, campus admins, and
              institution admins access data on a need-to-know basis for their
              role. Parents see only their own children's data; students see
              only their own.
            </li>
            <li>
              <strong>Service providers:</strong> We use trusted infrastructure
              partners for hosting (Emergent Cloud / AWS-region-India-based),
              email delivery, and payment processing. These partners are
              bound by contract to keep your data confidential and use it only
              to provide the service.
            </li>
            <li>
              <strong>Legal compliance:</strong> If required by law, court
              order, or a government request, we may disclose information to
              the extent legally necessary.
            </li>
            <li>
              <strong>Business transfers:</strong> If {PUBLISHER} is acquired
              or merges with another entity, your information may be
              transferred as part of that transaction, subject to the same
              privacy commitments.
            </li>
          </ul>

          <h2>4. Data Retention</h2>
          <p>
            We retain personal information for as long as your school
            subscription is active, and for a reasonable period thereafter
            (typically 90 days) to permit account recovery and legal
            compliance. Financial records are retained for the period required
            by applicable Indian tax and audit laws (currently 8 years).
            Schools can request permanent deletion of any student or staff
            record at any time; we honour valid deletion requests within 30
            days.
          </p>

          <h2>5. Data Security</h2>
          <ul>
            <li>All traffic is encrypted in transit via TLS (HTTPS).</li>
            <li>Passwords are stored using bcrypt one-way hashing — never in plain text.</li>
            <li>Databases are encrypted at rest.</li>
            <li>Role-based access controls, multi-tenant isolation, and per-campus data scoping are enforced at the API layer.</li>
            <li>We keep security logs to detect and investigate unauthorised access.</li>
          </ul>
          <p>
            No system is 100% secure. In the unlikely event of a data breach
            affecting your information, we will notify you and the relevant
            authorities in accordance with applicable law.
          </p>

          <h2>6. Your Rights</h2>
          <p>As a data principal under the Digital Personal Data Protection Act, 2023 (India), you have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you.</li>
            <li>Request correction of inaccurate data.</li>
            <li>Request deletion of your data (subject to legal retention obligations).</li>
            <li>Withdraw your consent to specific processing activities.</li>
            <li>Nominate another individual to exercise these rights on your behalf.</li>
            <li>Grievance redressal — write to us using the contact details below.</li>
          </ul>
          <p>
            To exercise any of these rights, email us at {" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from the address associated with your account, or contact your school administrator.
          </p>

          <h2>7. Cookies & Local Storage</h2>
          <p>
            The web version of Kampus 365 uses browser local-storage to keep
            you signed in and remember your interface preferences (like dark
            mode). We do not use third-party advertising or tracking cookies.
            The mobile app uses secure device storage for the same purpose.
          </p>

          <h2>8. Push Notifications</h2>
          <p>
            The mobile app may send push notifications for new notices,
            homework, fee reminders, and attendance events. You can disable
            push notifications from your device settings at any time without
            affecting other app functionality.
          </p>

          <h2>9. Third-Party Links</h2>
          <p>
            Notices and homework may contain links to third-party websites.
            Once you leave our app, this Privacy Policy no longer applies —
            please review the privacy notices of any site you visit.
          </p>

          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. If we make
            material changes, we will notify you via in-app notice or email
            at least 30 days before the change takes effect. Continued use of
            the service after that date constitutes acceptance of the updated
            policy.
          </p>

          <h2>11. Grievance Officer &amp; Contact</h2>
          <p>
            If you have questions, complaints, or wish to exercise your
            rights, please contact:
          </p>
          <address className="not-italic">
            <strong>{PUBLISHER}</strong>
            <br />
            App: {APP_NAME}
            <br />
            Support email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            <br />
            School office email: <a href={`mailto:${SCHOOL_EMAIL}`}>{SCHOOL_EMAIL}</a>
            <br />
            School phone: <a href={`tel:${SCHOOL_PHONE.replace(/\s/g, "")}`}>{SCHOOL_PHONE}</a>
            <br />
            Address: {SCHOOL_ADDRESS}
          </address>

          <p className="mt-8 text-xs text-slate-500">
            This policy was last reviewed on {LAST_UPDATED}. For the
            legally-binding version and jurisdiction, please refer to the
            English text hosted on this URL.
          </p>
        </section>

        <div className="mt-12 border-t border-slate-200 pt-6 text-sm text-slate-500 flex flex-wrap gap-4">
          <Link to="/legal/terms" className="hover:text-slate-900" data-testid="terms-link">
            Terms of Service
          </Link>
          <span>·</span>
          <Link to="/" className="hover:text-slate-900" data-testid="home-link">
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
